<template>
  <v-container fluid class="dashboard-container pa-0 pb-12">
    <!-- Header: Identity & Critical Pulse -->
    <v-sheet color="white" class="pa-6 pa-md-8 mb-6 border-bottom shadow-xs">
      <v-row align="center" justify="space-between">
        <v-col cols="12" lg="6">
          <div class="d-flex align-center">
            <v-avatar size="52" color="primary lighten-5" class="mr-4 rounded-xl">
              <v-icon color="primary">fas fa-truck-ramp-box</v-icon>
            </v-avatar>
            <div>
              <div class="d-flex align-center flex-wrap" style="gap: 8px">
                <h1 class="text-h5 font-weight-black primary--text tracking-tight mb-0">
                  Dashboard Entregas
                </h1>
                <v-chip v-if="asOf" x-small label color="blue-grey lighten-5" class="blue-grey--text text--darken-3 font-weight-medium">
                  <v-icon left x-small>fas fa-clock</v-icon>
                  Actualizado {{ formattedAsOf }}
                </v-chip>
              </div>
              <p class="body-2 grey--text text--darken-1 font-weight-medium mb-0 mt-1">
                Supervisión de órdenes de trabajo abiertas y compromisos de despacho · {{ asOfLabel }}
                · <span class="font-weight-bold primary--text">{{ selectedLineName }}</span>
              </p>
            </div>
          </div>
        </v-col>

        <!-- Critical Pulse Ticker & Action Buttons -->
        <v-col cols="12" lg="6" class="d-flex align-center justify-lg-end flex-wrap" style="gap: 12px">
          <!-- Critical Late Pulse Card -->
          <div v-if="kpis.late" class="daily-pulse-card d-flex align-center px-4 py-2 rounded-xl">
            <div class="pulse-indicator is-critical mr-3"></div>
            <div>
              <div class="caption font-weight-bold text-uppercase error--text text--darken-2">Pulso Atrasos</div>
              <div class="d-flex align-baseline" style="gap: 6px">
                <span class="font-weight-black text-body-1 grey--text text--darken-4">
                  {{ kpis.late.count }} OT
                </span>
                <span class="caption font-weight-bold error--text">
                  {{ kpis.late.m2 | number('0,0.0') }} m² comprometidos
                </span>
              </div>
            </div>
          </div>

          <v-btn
            outlined
            color="primary"
            class="rounded-xl px-4 font-weight-bold text-none shadow-xs"
            @click="$router.push({ name: 'CompromiseCalendar2' })"
          >
            <v-icon left small>fas fa-calendar-alt</v-icon>
            Calendario
          </v-btn>

          <v-btn
            outlined
            color="primary"
            class="rounded-xl px-4 font-weight-bold text-none shadow-xs"
            :loading="downloadingPdf"
            @click="downloadExecutivePdf"
          >
            <v-icon left small color="error">fas fa-file-pdf</v-icon>
            Exportar PDF
          </v-btn>

          <v-btn
            color="primary"
            depressed
            class="rounded-xl px-5 font-weight-bold text-none shadow-xs"
            :loading="loading"
            @click="load"
          >
            <v-icon left small>fas fa-sync-alt</v-icon>
            Actualizar
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>

    <div class="px-6 px-md-8">
      <!-- Error Alert -->
      <v-alert
        v-if="error"
        dense
        outlined
        type="error"
        class="mb-6 rounded-xl bg-white"
      >
        <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px">
          <span>{{ error }}</span>
          <v-btn small color="error" depressed class="rounded-lg text-none font-weight-bold" @click="load">
            <v-icon left small>fas fa-redo</v-icon>
            Reintentar
          </v-btn>
        </div>
      </v-alert>

      <!-- Control Bar: Horizonte & Filtros de Estado -->
      <v-card flat outlined class="filter-card pa-4 mb-6 shadow-xs">
        <v-row align="center" dense justify="space-between">
          <v-col cols="12" md="8" class="d-flex align-center flex-wrap" style="gap: 12px">
            <!-- Horizonte -->
            <div class="d-flex align-center flex-wrap" style="gap: 6px">
              <span class="caption font-weight-bold grey--text text--darken-2 mr-1 text-uppercase">
                Horizonte:
              </span>
              <v-btn
                v-for="h in [7, 14, 30]"
                :key="h"
                x-small
                depressed
                :color="horizon === h ? 'primary' : 'grey lighten-4'"
                :class="horizon === h ? 'white--text font-weight-bold' : 'grey--text text--darken-3 font-weight-medium'"
                class="rounded-lg text-none px-3 py-2"
                @click="horizon = h"
              >
                {{ h }} días
              </v-btn>
            </div>

            <v-divider vertical class="mx-2 hidden-sm-and-down" />

            <!-- Filtros rápidos de estado -->
            <div class="d-flex align-center flex-wrap" style="gap: 6px">
              <span class="caption font-weight-bold grey--text text--darken-2 mr-1 text-uppercase">
                Filtro rápido:
              </span>
              <v-btn
                v-for="st in statusOptions"
                :key="st.key"
                x-small
                depressed
                :color="statusFilter === st.key ? 'primary' : 'grey lighten-4'"
                :class="statusFilter === st.key ? 'white--text font-weight-bold' : 'grey--text text--darken-3 font-weight-medium'"
                class="rounded-lg text-none px-3 py-2"
                @click="toggleStatus(st.key)"
              >
                {{ st.label }}
              </v-btn>
            </div>
          </v-col>

          <v-col cols="12" md="4" class="d-flex align-center justify-md-end flex-wrap" style="gap: 8px">
            <v-chip v-if="kpis.without_date" x-small color="amber lighten-4" class="font-weight-bold amber--text text--darken-4">
              <v-icon left x-small color="amber darken-4">fas fa-exclamation-circle</v-icon>
              {{ kpis.without_date }} OT sin fecha comprometida
            </v-chip>
            <v-chip v-if="dayFilter" small close color="primary lighten-5" class="primary--text font-weight-bold" @click:close="dayFilter = null">
              Día: {{ formatDateShort(dayFilter) }}
            </v-chip>
          </v-col>
        </v-row>
        <v-row dense class="mt-2">
          <v-col cols="12" md="4">
            <v-select
              v-model="line"
              :items="businessLineOptions"
              item-value="id"
              item-text="name"
              label="Línea de negocio"
              prepend-inner-icon="fas fa-layer-group"
              outlined
              dense
              hide-details
            />
          </v-col>
        </v-row>
      </v-card>

      <!-- Loading Skeletons -->
      <v-row v-if="loading && !orders.length" dense class="mb-6">
        <v-col v-for="i in 4" :key="'sk-' + i" cols="12" sm="6" md="3">
          <v-skeleton-loader type="card" height="136" class="rounded-xl shadow-xs" />
        </v-col>
      </v-row>

      <template v-else>
        <!-- Hero KPIs: 4 Cards -->
        <v-row dense class="mb-6">
          <!-- Card 1: Atrasadas (North Star Crítico) -->
          <v-col cols="12" sm="6" md="3">
            <v-card
              flat
              class="hero-kpi-card h-100 is-clickable"
              :class="{ 'is-selected': statusFilter === 'late', 'is-critical-kpi': kpis.late.count > 0 }"
              @click="toggleStatus('late')"
            >
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label" :class="{ 'error--text': kpis.late.count > 0 }">
                    OT Atrasadas
                  </div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Órdenes de trabajo abiertas cuya fecha de entrega comprometida ya venció.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value" :class="kpis.late.count > 0 ? 'error--text text--darken-2' : 'grey--text text--darken-4'">
                  {{ kpis.late.count }} <span class="unit-text">OT</span>
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold" :class="kpis.late.count > 0 ? 'error--text text--darken-1' : 'grey--text text--darken-1'">
                      {{ kpis.late.m2 | number('0,0.0') }} m² comprometidos
                    </span>
                    <span class="caption font-weight-bold grey--text text--darken-2">
                      {{ kpis.late.qty | number('0,0') }} un
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Card 2: Hoy -->
          <v-col cols="12" sm="6" md="3">
            <v-card
              flat
              class="hero-kpi-card h-100 is-clickable"
              :class="{ 'is-selected': statusFilter === 'today' }"
              @click="toggleStatus('today')"
            >
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Compromiso Hoy</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Órdenes de trabajo con compromiso de entrega fijado para la jornada actual.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value warning--text text--darken-3">
                  {{ kpis.today.count }} <span class="unit-text">OT</span>
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">
                      {{ kpis.today.m2 | number('0,0.0') }} m² programados
                    </span>
                    <span class="caption font-weight-bold grey--text text--darken-2">
                      {{ kpis.today.qty | number('0,0') }} un
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Card 3: Esta Semana -->
          <v-col cols="12" sm="6" md="3">
            <v-card
              flat
              class="hero-kpi-card h-100 is-clickable"
              :class="{ 'is-selected': statusFilter === 'week' }"
              @click="toggleStatus('week')"
            >
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Esta Semana</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Órdenes de trabajo con fecha de entrega comprometida en los próximos 6 días.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value teal--text text--darken-3">
                  {{ kpis.week.count }} <span class="unit-text">OT</span>
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">
                      {{ kpis.week.m2 | number('0,0.0') }} m² en ventana
                    </span>
                    <span class="caption font-weight-bold grey--text text--darken-2">
                      {{ kpis.week.qty | number('0,0') }} un
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Card 4: Total OTs Abiertas -->
          <v-col cols="12" sm="6" md="3">
            <v-card
              flat
              class="hero-kpi-card h-100 is-clickable"
              :class="{ 'is-selected': statusFilter === 'all' && !dayFilter }"
              @click="statusFilter = 'all'; dayFilter = null"
            >
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Total OTs Comprometidas</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Total acumulado de órdenes de trabajo abiertas en seguimiento operativo.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value primary--text text--darken-3">
                  {{ kpis.open }} <span class="unit-text">OT</span>
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">
                      {{ kpis.prestage }} en preprensa
                    </span>
                    <span class="caption font-weight-bold grey--text text--darken-2">
                      {{ kpis.without_date }} sin fecha
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Compromisos por Línea de Negocio -->
        <v-card class="panel-card mb-6 shadow-xs" flat>
          <div class="d-flex align-center justify-space-between mb-1 flex-wrap" style="gap: 8px">
            <div class="panel-title">Por Línea de Negocio</div>
            <span class="caption grey--text font-weight-bold">Pendiente de entregar en m² y unidades según la línea</span>
          </div>
          <div class="panel-sub mb-4">Todas las OTs abiertas con fecha, sin aplicar el filtro de línea · clic en una fila para filtrar el dashboard</div>
          <div v-if="!lineBreakdown.length" class="empty-mini py-6 text-center">Sin OTs abiertas</div>
          <v-simple-table v-else dense class="line-table">
            <thead>
              <tr>
                <th>Línea</th>
                <th class="text-end">OTs</th>
                <th class="text-end">Atrasadas</th>
                <th class="text-end">m²</th>
                <th class="text-end">Unidades</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in lineBreakdown"
                :key="'l-' + row.id"
                class="line-row"
                :class="{ 'is-selected-line': String(line) === String(row.id) }"
                @click="toggleLine(row.id)"
              >
                <td class="font-weight-bold">{{ row.name }}</td>
                <td class="text-end">{{ row.count }}</td>
                <td class="text-end font-weight-bold" :class="row.late ? 'error--text' : 'grey--text'">{{ row.late }}</td>
                <td class="text-end font-weight-black">{{ row.m2 | number('0,0.0') }}</td>
                <td class="text-end font-weight-black">{{ row.qty | number('0,0') }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>

        <!-- Panel Carga de Entregas (Day Strip) -->
        <v-card class="panel-card mb-6 shadow-xs" flat>
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="panel-title">Carga y Flujo de Entregas</div>
            <span class="caption grey--text font-weight-bold">
              Ventana de {{ horizon }} días · Clic en un día para filtrar
            </span>
          </div>
          <div class="panel-sub mb-4">
            Monitoreo dinámico del volumen y cantidad de órdenes comprometidas por fecha de despacho
          </div>

          <div class="day-strip">
            <button
              v-for="day in visibleDays"
              :key="day.date"
              type="button"
              class="day-cell"
              :class="{
                'is-today': day.is_today,
                'is-empty': !day.count,
                'is-weekend': day.is_weekend,
                'is-selected': dayFilter === day.date,
              }"
              @click="toggleDay(day.date)"
            >
              <span class="day-name">{{ weekdayLabel(day.date) }}</span>
              <span class="day-num">{{ day.day }}</span>
              <span class="day-bar-track">
                <span class="day-bar" :style="{ height: barHeight(day) }"></span>
              </span>
              <span class="day-count">{{ day.count || '—' }}</span>
              <span class="day-m2">{{ day.count ? day.m2.toFixed(0) + ' m²' : '' }}</span>
            </button>
          </div>
        </v-card>

        <!-- Rankings Paralelos: Clientes & Vendedores (2 Columnas) -->
        <v-row dense class="mb-6">
          <!-- Columna 1: Clientes con más atrasos / carga -->
          <v-col cols="12" md="6">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Clientes con Mayor Compromiso / Atrasos</div>
                <span class="caption grey--text font-weight-bold">Top 8</span>
              </div>
              <div class="panel-sub mb-4">Clientes ordenados por nivel de criticidad y volumen m²</div>

              <div v-if="!customers.length" class="empty-mini py-6 text-center">
                Sin órdenes de trabajo abiertas con fecha registrada
              </div>
              <div
                v-for="row in customers"
                :key="row.name"
                class="rank-row"
              >
                <div class="d-flex align-center justify-space-between">
                  <span class="rank-name text-truncate" style="max-width: 260px">
                    {{ row.name }}
                  </span>
                  <span class="rank-val font-weight-black">
                    {{ row.m2 | number('0,0.0') }} m²
                  </span>
                </div>
                <div class="d-flex align-center justify-space-between mt-1">
                  <span class="rank-meta">
                    <span v-if="row.late" class="error--text font-weight-bold mr-1">
                      {{ row.late }} atrasada{{ row.late !== 1 ? 's' : '' }} ·
                    </span>
                    {{ row.count }} OT{{ row.count !== 1 ? 's' : '' }} en total
                  </span>
                </div>
                <div class="rank-track mt-1">
                  <div
                    class="rank-fill"
                    :class="row.late > 0 ? 'is-rose' : 'is-navy'"
                    :style="{ width: rankWidth(row.m2, maxCustomerM2) }"
                  ></div>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Columna 2: Vendedores -->
          <v-col cols="12" md="6">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Carga por Ejecutivo de Ventas</div>
                <span class="caption grey--text font-weight-bold">Top 8</span>
              </div>
              <div class="panel-sub mb-4">Supervisión comercial por cartera de órdenes comprometidas</div>

              <div v-if="!sellers.length" class="empty-mini py-6 text-center">
                Sin datos de ejecutivos registrados
              </div>
              <div
                v-for="row in sellers"
                :key="row.name"
                class="rank-row"
              >
                <div class="d-flex align-center justify-space-between">
                  <span class="rank-name text-truncate" style="max-width: 260px">
                    {{ row.name }}
                  </span>
                  <span class="rank-val font-weight-black">
                    {{ row.m2 | number('0,0.0') }} m²
                  </span>
                </div>
                <div class="d-flex align-center justify-space-between mt-1">
                  <span class="rank-meta">
                    <span v-if="row.late" class="error--text font-weight-bold mr-1">
                      {{ row.late }} atrasada{{ row.late !== 1 ? 's' : '' }} ·
                    </span>
                    {{ row.count }} OT{{ row.count !== 1 ? 's' : '' }} en total
                  </span>
                </div>
                <div class="rank-track mt-1">
                  <div
                    class="rank-fill is-teal"
                    :style="{ width: rankWidth(row.m2, maxSellerM2) }"
                  ></div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Tabla Detallada de Órdenes Comprometidas -->
        <v-card class="panel-card mb-8 shadow-xs" flat>
          <div class="d-flex align-center justify-space-between flex-wrap mb-4" style="gap: 16px">
            <div>
              <div class="panel-title">Órdenes de Trabajo Comprometidas</div>
              <div class="panel-sub">
                Mostrando {{ filteredOrders.length }} de {{ orders.length }} OTs abiertas
                <span v-if="statusFilter !== 'all'"> · Filtrado por: <strong>{{ statusLabelText }}</strong></span>
                <span v-if="dayFilter"> · Día: <strong>{{ formatDateShort(dayFilter) }}</strong></span>
              </div>
            </div>
            <v-text-field
              v-model="search"
              prepend-inner-icon="search"
              label="Buscar OT, cliente, proyecto, vendedor..."
              dense
              outlined
              hide-details
              clearable
              class="rounded-lg table-search"
            />
          </div>

          <v-data-table
            :headers="headers"
            :items="filteredOrders"
            :items-per-page="15"
            :footer-props="{ 'items-per-page-options': [10, 15, 25, 50] }"
            class="elevation-0 commit-table"
            no-data-text="No hay órdenes de trabajo para los filtros seleccionados"
          >
            <template v-slot:item.id="{ item }">
              <show-work-order :work_order_id="item.id" class="ot-link font-weight-black">
                OT-{{ item.id }}
              </show-work-order>
            </template>

            <template v-slot:item.customer="{ item }">
              <div class="font-weight-bold primary--text text-truncate" style="max-width: 220px">
                {{ item.customer || '—' }}
              </div>
              <div class="caption grey--text text--darken-1 text-truncate" style="max-width: 220px">
                {{ item.title || 'Sin proyecto' }}
              </div>
            </template>

            <template v-slot:item.seller="{ item }">
              <span class="caption font-weight-bold grey--text text--darken-2">
                {{ item.seller || 'Sin vendedor' }}
              </span>
            </template>

            <template v-slot:item.delivery_date="{ item }">
              <span class="caption font-weight-medium">
                {{ item.delivery_date ? formatDateShort(item.delivery_date) : 'Sin fecha' }}
              </span>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip
                x-small
                label
                :color="statusChipColor(item.status)"
                dark
                class="font-weight-bold px-3"
              >
                {{ statusLabel(item) }}
              </v-chip>
            </template>

            <template v-slot:item.m2="{ item }">
              <div class="text-right font-weight-black">
                {{ item.m2 | number('0,0.0') }} m²
              </div>
            </template>

            <template v-slot:item.progress="{ item }">
              <v-chip x-small color="grey lighten-3" class="font-weight-bold">
                {{ item.products_done }}/{{ item.products_total }}
              </v-chip>
            </template>

            <template v-slot:item.in_prestage="{ item }">
              <v-chip v-if="item.in_prestage" x-small outlined color="orange darken-2" class="font-weight-bold">
                Preprensa
              </v-chip>
              <span v-else class="caption grey--text font-weight-medium">Planta</span>
            </template>
          </v-data-table>
        </v-card>
      </template>
    </div>
  </v-container>
</template>

<script>
import moment from 'moment'
import { fetchDeliveryCommitments } from '@/api/reports'
import { generateDeliveryDashboardPdf } from '@/doc_generators/deliveryDashboardReport'
import businessLineFilter from '@/mixins/businessLineFilter'

export default {
  name: 'DeliveryCommitmentsDashboard',
  mixins: [businessLineFilter],
  data() {
    return {
      loading: false,
      downloadingPdf: false,
      error: '',
      asOf: '',
      timezone: 'America/Santiago',
      kpis: {
        late: { count: 0, m2: 0, qty: 0 },
        today: { count: 0, m2: 0, qty: 0 },
        week: { count: 0, m2: 0, qty: 0 },
        later: { count: 0, m2: 0, qty: 0 },
        open: 0,
        prestage: 0,
        without_date: 0,
      },
      statusOptions: [
        { key: 'all', label: 'Todas' },
        { key: 'late', label: 'Atrasadas' },
        { key: 'today', label: 'Hoy' },
        { key: 'week', label: 'Esta semana' },
        { key: 'later', label: 'Próximas' },
      ],
      days: [],
      customers: [],
      sellers: [],
      orders: [],
      horizon: 14,
      statusFilter: 'all',
      dayFilter: null,
      search: '',
      headers: [
        { text: 'N° OT', value: 'id', width: '100px', sortable: true },
        { text: 'Cliente / Proyecto', value: 'customer', sortable: true },
        { text: 'Vendedor', value: 'seller', sortable: true },
        { text: 'F. Entrega', value: 'delivery_date', width: '110px', sortable: true },
        { text: 'Estado', value: 'status', width: '120px', sortable: true },
        { text: 'Volumen', value: 'm2', align: 'end', width: '100px', sortable: true },
        { text: 'Prod.', value: 'progress', align: 'center', width: '90px', sortable: false },
        { text: 'Etapa', value: 'in_prestage', align: 'center', width: '100px', sortable: true },
      ],
      timer: null,
    }
  },
  computed: {
    parameters() {
      return this.$store.getters.getCompany || {}
    },
    formattedAsOf() {
      if (!this.asOf) return ''
      return moment(this.asOf).format('DD/MM/YYYY')
    },
    asOfLabel() {
      if (!this.asOf) return 'Cargando...'
      return moment(this.asOf).locale('es').format('dddd D [de] MMMM [de] YYYY')
    },
    statusLabelText() {
      const match = this.statusOptions.find((st) => st.key === this.statusFilter)
      return match ? match.label : this.statusFilter
    },
    visibleDays() {
      return (this.days || []).slice(0, this.horizon)
    },
    maxDayCount() {
      return Math.max(1, ...this.visibleDays.map((d) => d.count || 0))
    },
    maxCustomerM2() {
      return Math.max(1, ...this.customers.map((r) => r.m2 || 0))
    },
    maxSellerM2() {
      return Math.max(1, ...this.sellers.map((r) => r.m2 || 0))
    },
    filteredOrders() {
      const needle = (this.search || '').toLowerCase().trim()
      return this.orders.filter((item) => {
        if (this.statusFilter !== 'all' && item.status !== this.statusFilter) return false
        if (this.dayFilter && item.delivery_date !== this.dayFilter) return false
        if (!needle) return true
        return [item.id, item.customer, item.title, item.seller, item.purchase_order]
          .some((value) => String(value || '').toLowerCase().includes(needle))
      })
    },
  },
  watch: {
    line() {
      this.dayFilter = null
      this.load()
    }
  },
  created() {
    this.load()
    this.timer = setInterval(this.load, 60000)
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    async load() {
      this.loading = true
      this.error = ''
      try {
        const data = await fetchDeliveryCommitments(this.lineQuery().replace(/^&/, ''))
        this.asOf = data.as_of
        this.setLineBreakdown(data.lines)
        this.timezone = data.timezone || 'America/Santiago'
        this.kpis = data.kpis || this.kpis
        this.days = data.days || []
        this.customers = data.customers || []
        this.sellers = data.sellers || []
        this.orders = data.orders || []
      } catch (e) {
        this.error = 'No se pudo cargar el Dashboard de Entregas.'
      } finally {
        this.loading = false
      }
    },
    async downloadExecutivePdf() {
      this.downloadingPdf = true
      try {
        await generateDeliveryDashboardPdf({
          company: this.parameters,
          asOf: this.asOf,
          timezone: this.timezone,
          kpis: this.kpis,
          days: this.days,
          customers: this.customers,
          sellers: this.sellers,
          orders: this.filteredOrders,
          horizon: this.horizon,
          statusFilter: this.statusFilter,
          lineName: this.selectedLineName,
          lines: this.lineBreakdown,
        })
      } catch (e) {
        console.error('Error generando PDF de entregas:', e)
        this.$store.dispatch('showNotification', {
          text: 'Error al generar el PDF del Dashboard de Entregas',
          color: 'error',
        })
      } finally {
        this.downloadingPdf = false
      }
    },
    toggleStatus(key) {
      this.statusFilter = this.statusFilter === key ? 'all' : key
      this.dayFilter = null
    },
    toggleDay(date) {
      this.dayFilter = this.dayFilter === date ? null : date
      this.statusFilter = 'all'
    },
    weekdayLabel(date) {
      return moment(date).locale('es').format('ddd').replace('.', '').toUpperCase()
    },
    formatDateShort(date) {
      return moment(date).format('DD/MM/YYYY')
    },
    barHeight(day) {
      if (!day.count) return '4px'
      return Math.max(8, Math.round((day.count / this.maxDayCount) * 36)) + 'px'
    },
    rankWidth(value, max) {
      return Math.max(6, Math.round((value / max) * 100)) + '%'
    },
    statusChipColor(status) {
      if (status === 'late') return '#b91c1c'
      if (status === 'today') return '#c2410c'
      if (status === 'week') return '#0f766e'
      return '#64748b'
    },
    statusLabel(item) {
      if (item.status === 'late') return 'Atraso ' + Math.abs(item.days) + 'd'
      if (item.status === 'today') return 'Hoy'
      if (item.days === 1) return 'Mañana'
      return 'En ' + item.days + 'd'
    },
  },
}
</script>

<style scoped>
.dashboard-container {
  background-color: #f8fafc;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.border-bottom {
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.shadow-xs {
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.02);
}

.tracking-tight {
  letter-spacing: -0.02em;
}

/* Critical Pulse Ticker */
.daily-pulse-card {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}

.pulse-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
}

.pulse-indicator.is-critical {
  background: #e11d48;
  box-shadow: 0 0 0 3px rgba(225, 29, 72, 0.2);
  animation: pulse-critical 2s infinite ease-in-out;
}

@keyframes pulse-critical {
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(225, 29, 72, 0.4); }
  70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(225, 29, 72, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(225, 29, 72, 0); }
}

/* Filters */
.filter-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

/* Hero KPIs */
.hero-kpi-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.hero-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.hero-kpi-card.is-selected {
  border-color: #0284c7 !important;
  box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.25) !important;
}

.hero-kpi-card.is-critical-kpi {
  border-color: #fecaca;
  background: #fef2f2;
}

.is-clickable {
  cursor: pointer;
}

.kpi-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.kpi-value {
  font-size: 1.55rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.2;
}

.unit-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: #64748b;
}

.kpi-footer {
  border-top: 1px solid #f1f5f9;
}

/* Panels */
.panel-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 22px;
}

.panel-title {
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.panel-sub {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  margin-top: 2px;
}

/* Day Strip */
.day-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(56px, 1fr));
  gap: 6px;
}

.day-cell {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 10px;
  padding: 8px 4px 6px;
  text-align: center;
  cursor: pointer;
  min-height: 108px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.day-cell:hover {
  border-color: #0284c7;
}

.day-cell.is-selected {
  border-color: #0284c7;
  background: #f0f9ff;
}

.day-cell.is-today {
  box-shadow: inset 0 0 0 2px #0284c7;
}

.day-cell.is-weekend {
  background: #f8fafc;
}

.day-name {
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  color: #94a3b8;
}

.day-num {
  font-size: 16px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1.2;
}

.day-bar-track {
  height: 36px;
  width: 10px;
  display: flex;
  align-items: flex-end;
  margin: 4px 0 2px;
}

.day-bar {
  width: 10px;
  background: #0f766e;
  border-radius: 3px;
}

.day-cell.is-empty .day-bar {
  background: #e2e8f0;
}

.day-count {
  font-size: 11px;
  font-weight: 800;
  color: #1e293b;
}

.day-m2 {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  min-height: 14px;
}

/* Rankings */
.rank-row {
  padding: 8px 10px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: #f8fafc;
}

.rank-name {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}

.rank-meta {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

.rank-val {
  font-size: 12px;
  color: #0f172a;
}

.rank-track {
  height: 5px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.rank-fill {
  height: 100%;
  background: #0284c7;
  border-radius: 999px;
}

.rank-fill.is-rose { background: #e11d48; }
.rank-fill.is-navy { background: #0f172a; }
.rank-fill.is-teal { background: #0d9488; }

.empty-mini {
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
}

.table-search {
  width: 320px;
  max-width: 100%;
}

.commit-table >>> th {
  font-size: 11px !important;
  font-weight: 800 !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b !important;
}

.ot-link {
  font-weight: 800;
  color: #0284c7;
  cursor: pointer;
}

.ot-link:hover {
  text-decoration: underline;
}

@media (max-width: 960px) {
  .table-search {
    width: 100%;
  }
}

.line-table >>> th {
  font-size: 11px !important;
  font-weight: 800 !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b !important;
}

.line-row {
  cursor: pointer;
}

.is-selected-line {
  background: #f0fdf4;
  box-shadow: inset 3px 0 0 #00a650;
}
</style>
